import {
  Redirect,
  Route,
  RouteProps,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import Login from "./pages/login/LogIn";
import HSALogin from "./pages/homeschool/login/LogIn";
import Verify from "./pages/verify/Verify";
import AdminVerify from "./pages/verify/AdminVerify";
import Dashboard from "./pages/dashboard/NewDashboard";
import ProgramDetails from "./pages/program-details/ProgramDetails";
import React, { PropsWithChildren, useEffect } from "react";
import { useAuthToken } from "./hooks/useAuthToken";
import ForgotPassword from "./pages/forgot-password/ForgotPassword";
import ForgotPasswordConfirm from "./pages/forgot-password-confirm/ForgotPasswordConfirm";
import Registration from "./pages/registration/Registration";
import Expiry from "./pages/expiryRedirect/Expiry";
import HSARegistration from "./pages/homeschool/registration/Registration";
import HSAForgotPassword from "./pages/homeschool/forgot-password/HSAForgotPassword";
import HSAChangePassword from "./pages/homeschool/change-password/HSAChangePassword";
import HSAResetPassword from "./pages/homeschool/reset-password/HSAResetPassword";
import HSACreatePassword from "./pages/homeschool/create-password/HSACreatePassword";
import Profile from "./pages/profile/Profile";
import { UserRoleEnum } from "./types";
import { useUser } from "./hooks/useUser";
import AdminUsers from "./pages/admin/users/AdminUsers";
import SummaryReports from "./pages/admin/reports/SummaryReports";
import SuperAdminAccounts from "./pages/super-admin/accounts/SuperAdminAccounts";
import SuperAdminUsers from "./pages/super-admin/users/SuperAdminUsers";
import SuperAdminHeUsers from "./pages/super-admin/heusers/SuperAdminHeUsers";
import SuperAdminHeLinks from "./pages/super-admin/helinks/SuperAdminHeLinks";
import SuperCourses from "./pages/super-admin/courses/SuperCourses";
import CourseCustomize from "./pages/super-admin/courses/CourseCustomize";
import UnitsView from "./pages/super-admin/courses/CourseUnits";
import EditSchool from "./pages/super-admin/edit-school/EditSchool";
import SchoolCoursesCustomize from "./pages/super-admin/edit-school/SchoolCoursesCustomize";
import SuperAccountSchools from "./pages/super-admin/schools/Schools";
import SuperCourseNew from "./pages/super-admin/course/new/CourseNew";
import SuperLessonNew from "./pages/super-admin/lessons/new/SuperLessonNew";
import SuperCourseEdit from "./pages/super-admin/course/edit/CourseEdit";
import { useIntercom, IntercomProps } from "react-use-intercom";
// import EditSpecialCoursePage from './pages/super-admin/special-course/edit/EditSpecialCoursePage';
import { useIntercomIdentity } from "./hooks/useIntercomIdentity";
import { useAnalytics } from "./hooks/useAnalytics";
import FeedbackFormPage from "./pages/feedback-form/FeedbackFormPage";
import ParentLogin from "./pages/parent/login/login";
import ParentDashboard from "./pages/parent/dashboard/dashboard";
import Unsubscribe from "./pages/unsubscribe/Unsubscribe";
import { useAuthTokenRevalidation } from "./hooks/useAuthTokenRevalidation";
import Survey from "./pages/survey/Survey";
import SuperUnits from "./pages/super-admin/units/SuperUnits";
import SuperUnitsNew from "./pages/super-admin/unit/new/SuperUnitNew";
import SuperUnitsEdit from "./pages/super-admin/unit/edit/UnitEdit";
import SuperGroups from "./pages/super-admin/groups/SuperGroups";
import AddChildren from "./pages/homeschool/add-children/addChildren";
import PlatformElements from "./pages/super-admin/administrative/PlatformElements";
import StudyPlan from "./pages/homeschool/studyplan/StudyPlan";
import HSAProfile from "./pages/homeschool/profile/Profile";
import SurveyExports from "./pages/super-admin/surveys/SurveyExports";
import ManualReportGeneration from "./pages/super-admin/report-management/ManualReportGeneration";
import EmailConfirm from "./pages/registration/EmailConfirm";
// import Maintain from './pages/maintain/Maintain';
// import EditMyCourses from './pages/standalone/MyCourses';
import GettingStarted from "./pages/standalone/GettingStarted";
import Caregiver from './pages/standalone/Caregiver';
import SuperLessons from './pages/super-admin/lessons/SuperLessons';
import SuperLessonEdit from './pages/super-admin/lessons/edit/LessonEdit';
import AdminDashboard from './pages/admin/dashboard/Dashboard';
import SuperAdminDashboard from './pages/super-admin/dashboard/Dashboard';
import AdminLessonProgress from './pages/lesson-progress/LessonProgress';
import { truncateEmail } from './utils/helpers';
import { IntercomCheckListID } from './constants';
import { useRolesPermissions } from './hooks';
import QuizBuilder from './pages/quizbuilder/QuizBuilder';
import ViewLesson from './pages/view-lesson/viewLesson';
import ViewStandaloneLesson from './pages/view-standalone-lesson/viewStandaloneLesson';
import Spinner from './components/spinner/Spinner';
import AdminUsage from './pages/admin/usage/UsageAnalytics';
import SuperAdminUsage from './pages/super-admin/usage/UsageAnalytics';
import LessonLibrary from './pages/lessonlibrary/LessonLibrary';
import Transitions from './pages/transitions/Transitions';
import WellbeingFoundations from './pages/wellbeingfoundations/WellbeingFoundations';
import SentryTrackingManagement from './pages/super-admin/sentry/SentryTrackingManagement';
import SuperCollections from "./pages/super-admin/collections/SuperCollections";
import CollectionEdit from "./pages/super-admin/collections/edit/CollectionEdit";
import CollectionCustomize from "./pages/super-admin/collections/CollectionCustomize";
import SuperMultiCollections from "./pages/super-admin/multi-collections/MultiCollections";
import MultiCollectionCustomize from "./pages/super-admin/multi-collections/MultiCollectionCustomize";
import StudentLinkPage from "./pages/student-link/StudentLinkPage";
import SuperProgramManagement from "./pages/super-admin/program-management/ProgramManagement";
import ProgramEdit from "./pages/super-admin/program-management/edit/ProgramEdit";
import Program from './pages/lessonlibrary/ProgramDetails';
import { logger } from './utils/logger';
import PageViewTracker from "./utils/pageViewTracker";

type OPRouteProps = RouteProps & { permissions: UserRoleEnum[] };

function PrivateComponentWrapper({ children, user }: PropsWithChildren & { user: any }) {
  useAuthTokenRevalidation();
  const { boot, update, isOpen } = useIntercom();
  const identity = useIntercomIdentity();
  const { identify } = useAnalytics();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const fullPath = pathname + search;
  const isUser = useRolesPermissions([UserRoleEnum.User]);
  
  // No need for token revalidation state since we're not using SentryProvider here anymore
  useEffect(() => {
    logger.info('[Sentry] User authenticated - Sentry tracking is now centralized in App.tsx');
  }, []);

  useEffect(() => {
    if (isUser && fullPath.indexOf("/admin") === 0) {
      return () => {
        history.push(fullPath.replace("/admin", ""));
      };
    }
  }, [isUser, fullPath, history]);

  useEffect(() => {
    if (boot) {
      boot({
        customAttributes: {
          horizontal_padding: 25,
        },
      });
    }
  }, [boot]);

  useEffect(() => {
    if ((window as any).Intercom && isOpen) {
      (window as any).Intercom("startChecklist", IntercomCheckListID);
    }
  }, [isOpen]);



  useEffect(() => {
    const identifyUserWhenReady = async () => {
      // Wait for both user data and segment to be available
      if (!user?.id) return;
      
      logger.debug(`Preparing to identify user ${user.id}`);
      let createdAt: number | undefined;
      if (user.createdAt) {
        createdAt = Math.round(Number(user.createdAt) / 1000);
      }
      const userProfile = {
        name: user.firstName,
        email: user.email,
        userId: (user.id || "").slice(3),
        createdAt,
        role: user.role,
        account: user.customer?.name,
        country: user.customer?.country,
        schools: (user.schools?.map((s) => s?.name) ?? []).join(", "),
        school_id: (user.schools?.map((s) => s?.id) ?? []).join(", "),
        subject: user.subject ?? "",
        position: user.position?.name ?? "",
        fullName: `${user.firstName || ""} ${user.lastName || ""}`,
        trial: user.customer?.trial,
        customer: user.customer?.name,
        customer_id: user.customer?.id,
        account_id: user.customer?.id,
        status: user.status,
      };

      await identify(user.id, userProfile);
      logger.debug(`User ${user.id} identification process completed`);
    };

    identifyUserWhenReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, identify]);

  useEffect(() => {
    if (user?.id && identity) {
      let createdAt: number | undefined = undefined;
      if (user.createdAt) {
        createdAt = Math.round(Number(user.createdAt) / 1000);
      }

      const userProps: Partial<IntercomProps> = {
        name: user.firstName,
        userId: (user.id || "").slice(3),
        createdAt,
        email: truncateEmail(user.email),
      };

      const addProps = {
        role: user.role,
        account: user.customer?.name,
        country: user.customer?.country,
        schools: (user.schools?.map((s) => s?.name) ?? []).join(", "),
        school_id: (user.schools?.map((s) => s?.id) ?? []).join(", "),
        subject: user.subject ?? "",
        position: user.position?.name ?? "",
        fullName: `${user.firstName || ""}`,
        email: user.email,
        trial: user.customer?.trial,
        customer: user.customer?.name,
        customer_id: user.customer?.id,
        account_id: user.customer?.id,
        status: user.status,
        $email: truncateEmail(user.email),
      };

      update({
        ...userProps,
        userHash: identity,
        customAttributes: {
          ...addProps,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, identity, update]);

  // Return children directly - SentryProvider is now centralized in App.tsx
  return <>{children}</>;
}

function PublicComponetWrapper({ children }: PropsWithChildren) {
  const { boot, isOpen } = useIntercom();

  useEffect(() => {
    if (boot) boot({ customAttributes: { horizontal_padding: 25 } });
  }, [boot]);

  useEffect(() => {
    if ((window as any).Intercom && isOpen) {
      (window as any).Intercom("startChecklist", IntercomCheckListID);
    }
  }, [isOpen]);
  
  // Log that Sentry is now centralized
  useEffect(() => {
    logger.info('[Sentry] Public route - Sentry tracking is now centralized in App.tsx');
  }, []);

  // Return children directly - SentryProvider is now centralized in App.tsx
  return <>{children}</>;
}

function PrivateRoute({
  component: Component,
  permissions,
  ...rest
}: OPRouteProps) {
  const { token } = useAuthToken();
  const { pathname, search } = useLocation();
  const fullPath = (pathname + search).replace("?", "&");
  const [user, { loading }] = useUser();
  const isAuthenticated = !!token;

  if (!Component) return null;

  if (!isAuthenticated) {
    return (
      <Route
        {...rest}
        render={(props) => (
          <Redirect
            to={{
              pathname: "/login",
              search: fullPath ? `?returnUrl=${fullPath}` : "",
              state: { from: props.location },
            }}
          />
        )}
      />
    );
  }
  if (!user || loading) {
    return (
      <div className="op-centered">
        <Spinner />
      </div>
    );
  }
  if (permissions.includes(user?.role as UserRoleEnum)) {
    
    return (
      <Route
        {...rest}
        render={(props) => (
          <PrivateComponentWrapper user={user}>
            <Component {...props} />
          </PrivateComponentWrapper>
        )}
      />
    );
  }
  return <Route {...rest} render={(props) => <Redirect to={"/"} />} />;
}

function AuthRoute({ component: Component, ...rest }: RouteProps) {
  const { token } = useAuthToken();
  if (!Component) return null;
  const isAuthenticated = !!token;

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? (
          <Redirect to={"/dashboard"} />
        ) : (
          <PublicComponetWrapper>
            <Component {...props} />
          </PublicComponetWrapper>
        )
      }
    />
  );
}

const HsaRoute = (props: RouteProps) => {
  const { hsaToken } = useAuthToken();
  return hsaToken ? (
    <Route {...props} />
  ) : (
    <Redirect push to={"/homeschool/login"} />
  );
};

const MainPages = () => {
  return (
    <>
      <PageViewTracker />
      <Switch>
        <Route
          exact={true}
          path={"/feedbackform/:id?"}
          component={FeedbackFormPage}
        />
        <Route exact={true} path={"/ff/:id?"} component={FeedbackFormPage} />
        <Route exact={true} path={"/registration"} component={Registration} />
        <Route exact={true} path={"/expiryRedirect"} component={Expiry} />
        <Route exact={true} path={"/survey"} component={Survey} />
        <Route exact={true} path={"/student/:id?"} component={StudentLinkPage} />
        <AuthRoute
          exact={true}
          path={"/forgot-password"}
          component={ForgotPassword}
        />
        <AuthRoute
          exact={true}
          path={"/reset-password-confirm"}
          component={ForgotPasswordConfirm}
        />
        <AuthRoute path={"/login"} component={Login} />
        <AuthRoute path={"/verify"} component={Verify} />
        <AuthRoute path={"/admin/verify"} component={AdminVerify} />
        <AuthRoute path={"/email-confirm"} component={EmailConfirm} />

        <PrivateRoute
          exact={true}
          path={"/dashboard"}
          component={Dashboard}
          permissions={[
            UserRoleEnum.SuperAdmin,
            UserRoleEnum.DistrictAdmin,
            UserRoleEnum.Admin,
            UserRoleEnum.ContentAdmin,
            UserRoleEnum.User,
          ]}
        />
        <PrivateRoute
          exact={true}
          path={'/program/:id'}
          component={Program}
          permissions={[
            UserRoleEnum.SuperAdmin,
            UserRoleEnum.DistrictAdmin,
            UserRoleEnum.Admin,
            UserRoleEnum.ContentAdmin,
            UserRoleEnum.User,
          ]}
        />
        <PrivateRoute
          exact={true}
          path={"/profile"}
          component={Profile}
          permissions={[
            UserRoleEnum.SuperAdmin,
            UserRoleEnum.DistrictAdmin,
            UserRoleEnum.Admin,
            UserRoleEnum.ContentAdmin,
            UserRoleEnum.User,
          ]}
        />
        <PrivateRoute
          exact={true}
          path={"/admin/users"}
          permissions={[UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin]}
          component={AdminUsers}
        />
        <PrivateRoute
          exact={true}
          path={"/summaryreports"}
          permissions={[
            UserRoleEnum.Admin,
            UserRoleEnum.DistrictAdmin,
            UserRoleEnum.User,
          ]}
          component={SummaryReports}
        />
        <PrivateRoute
          exact={true}
          path={"/units/studentvoiceseries"}
          permissions={[UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.User, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={AdminDashboard}
        />
        <PrivateRoute
          exact={true}
          path={"/lessonprogress"}
          permissions={[
            UserRoleEnum.User,
            UserRoleEnum.SuperAdmin,
            UserRoleEnum.Admin,
            UserRoleEnum.DistrictAdmin,
            UserRoleEnum.ContentAdmin,
          ]}
          component={AdminLessonProgress}
        />
        <PrivateRoute
          exact={true}
          path={"/super/programs"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperAdminDashboard}
        />
        <PrivateRoute
          exact={true}
          path={"/super/survey-exports"}
          permissions={[UserRoleEnum.SuperAdmin]}
          component={SurveyExports}
        />
        <PrivateRoute
          exact={true}
          path={"/super/report-management"}
          permissions={[UserRoleEnum.SuperAdmin]}
          component={ManualReportGeneration}
        />
        <PrivateRoute
          exact={true}
          path={"/super/accounts"}
          permissions={[UserRoleEnum.SuperAdmin]}
          component={SuperAdminAccounts}
        />
        <PrivateRoute
          exact={true}
          path={"/super/accounts/:id/schools"}
          permissions={[UserRoleEnum.SuperAdmin]}
          component={SuperAccountSchools}
        />
        <PrivateRoute
          exact={true}
          path={"/super/users"}
          permissions={[UserRoleEnum.SuperAdmin]}
          component={SuperAdminUsers}
        />
        {/* <PrivateRoute exact={true} path={'/super/users2'} permissions={[UserRoleEnum.SuperAdmin]} component={BulkUsersImportTable} /> */}
        <PrivateRoute
          exact={true}
          path={"/super/courses"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperCourses}
        />
        <PrivateRoute
          exact={true}
          path={"/super/courses/customize"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={CourseCustomize}
        />
        <PrivateRoute
          exact={true}
          path={"/super/courses/units/:id"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={UnitsView}
        />
        <PrivateRoute
          exact={true}
          path={"/super/school/edit"}
          permissions={[UserRoleEnum.SuperAdmin]}
          component={EditSchool}
        />
        <PrivateRoute
          exact={true}
          path={"/super/school/edit/courses"}
          permissions={[UserRoleEnum.SuperAdmin]}
          component={SchoolCoursesCustomize}
        />
        <PrivateRoute
          exact={true}
          path={"/super/courses/new"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperCourseNew}
        />
        <PrivateRoute
          exact={true}
          path={"/super/lessons"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperLessons}
        />
        <PrivateRoute
          exact={true}
          path={"/super/lessons/new"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperLessonNew}
        />
        <PrivateRoute
          exact={true}
          path={"/super/lessons/edit/:id"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperLessonEdit}
        />
        <PrivateRoute
          exact={true}
          path={"/super/courses/edit/:id"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperCourseEdit}
        />
        <PrivateRoute
          exact={true}
          path={"/super/elements/"}
          permissions={[UserRoleEnum.SuperAdmin]}
          component={PlatformElements}
        />
        {/* <PrivateRoute exact={true} path={'/super/special-courses/edit/:id'} permissions={[UserRoleEnum.SuperAdmin]} component={EditSpecialCoursePage} /> */}
        <PrivateRoute exact={true} path={'/super/groups'} permissions={[UserRoleEnum.SuperAdmin]} component={SuperGroups} />
        <PrivateRoute
          exact={true}
          path={"/super/collections"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperCollections}
        />
        <PrivateRoute
          exact={true}
          path={"/super/collections/edit/:id"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={CollectionEdit}
        />
        <PrivateRoute
          exact={true}
          path={"/super/collections/customize/:id"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={CollectionCustomize}
        />
        <PrivateRoute
          exact={true}
          path={"/super/multi-collections"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperMultiCollections}
        />
        <PrivateRoute
          exact={true}
          path={"/super/multi-collections/customize/:id"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={MultiCollectionCustomize}
        />
        <PrivateRoute
          exact={true}
          path={"/super/program-management"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={SuperProgramManagement}
        />
        <PrivateRoute
          exact={true}
          path={"/super/program-management/edit/:id"}
          permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]}
          component={ProgramEdit}
        />
        <PrivateRoute exact={true} path={'/super/units'} permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={SuperUnits} />
        <PrivateRoute exact={true} path={'/super/units/new'} permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={SuperUnitsNew} />
        <PrivateRoute exact={true} path={'/super/units/edit/:id'} permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={SuperUnitsEdit} />
        <PrivateRoute exact={true} path={'/super/helinks'} permissions={[UserRoleEnum.SuperAdmin]} component={SuperAdminHeLinks} />
        <PrivateRoute exact={true} path={'/super/heusers'} permissions={[UserRoleEnum.SuperAdmin]} component={SuperAdminHeUsers} />
        <PrivateRoute exact={true} path={'/standalone/lessons'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={LessonLibrary} />
        <PrivateRoute exact={true} path={'/lessons'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={LessonLibrary} />
        <PrivateRoute exact={true} path={'/lessons/transitions'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={Transitions} />
        <PrivateRoute exact={true} path={'/wellbeingfoundations'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={WellbeingFoundations} />
        <PrivateRoute exact={true} path={'/getting-started'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={GettingStarted} />
        <PrivateRoute exact={true} path={'/caregiver'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={Caregiver} />
        <PrivateRoute exact={true} path={'/assessments'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin]} component={QuizBuilder} />
        <PrivateRoute exact={true} path={'/programs'} permissions={[UserRoleEnum.User]} component={AdminDashboard} />
        <PrivateRoute exact={true} path={'/admin/usage'} permissions={[UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin]} component={AdminUsage} />
        <PrivateRoute exact={true} path={'/super/usage'} permissions={[UserRoleEnum.SuperAdmin]} component={SuperAdminUsage} />
        <PrivateRoute exact={true} path={'/super/sentry'} permissions={[UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={SentryTrackingManagement} />
        <PrivateRoute exact={true} path={'/standalone/:id'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={ViewStandaloneLesson} />
        <PrivateRoute exact={true} path={'/:id'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={ViewLesson} />
        <PrivateRoute exact={true} path={'/:id/:id'} permissions={[UserRoleEnum.User, UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin, UserRoleEnum.SuperAdmin, UserRoleEnum.ContentAdmin]} component={ViewLesson} />
        <Redirect to={'/dashboard'}/>
      </Switch>
    </>
  );
};

const HomeSchoolPages = () => {
  return (
    <Switch>
      <Route path={"/homeschool/login"} component={HSALogin} />
      <Route path={"/homeschool/registration"} component={HSARegistration} />
      <Route path={"/homeschool/profile"} component={HSAProfile} />
      <Route
        path={"/homeschool/create-password"}
        component={HSACreatePassword}
      />
      <Route
        path={"/homeschool/forgot-password"}
        component={HSAForgotPassword}
      />
      <Route
        path={"/homeschool/reset-password-confirm"}
        component={HSAResetPassword}
      />
      <Route
        exact={true}
        path={"/homeschool/studyplan"}
        component={StudyPlan}
      />
      <HsaRoute
        exact={true}
        path={"/homeschool/change-password"}
        component={HSAChangePassword}
      />
      <HsaRoute
        exact={true}
        path={"/homeschool/add-child"}
        component={AddChildren}
      />
    </Switch>
  );
};

const AnonymousPages = () => {
  return (
    <Switch>
      <Route
        exact={true}
        path={"/feedbackform/:id?"}
        component={FeedbackFormPage}
      />
      <Route path={"/unsubscribe/:id"} component={Unsubscribe} />
      <Route exact={true} path={"/parent/login"} component={ParentLogin} />
      <Route
        exact={true}
        path={"/parent/dashboard"}
        component={ParentDashboard}
      />
      <Route
        exact={true}
        path={"/parent/program/:id"}
        component={ProgramDetails}
      />
      <Route
        exact={true}
        path={"/parent/specialProgram/:id"}
        component={ProgramDetails}
      />
      <Redirect to={"/"} />
    </Switch>
  );
};

const anonymusPages: string[] = ["/feedbackform", "/parent", "/unsubscribe"];
export const AuthGate = () => {
  const location = document.location;
  const oneOf = anonymusPages.some((page) =>
    location.pathname.startsWith(page)
  );
  const hsaPg = location.pathname.startsWith("/homeschool");
  return oneOf ? (
    <AnonymousPages />
  ) : hsaPg ? (
    <HomeSchoolPages />
  ) : (
    <MainPages />
  );
};
